<template>
  <div v-if="settings" class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow">
    <div class="px-5 py-5 sm:py-6">
      <h3 class="text-base font-semibold text-gray-900">{{ $gettext("Extraction de documents") }}</h3>
      <p class="mt-1 text-sm text-gray-500">
        {{
          $gettext(
            "Nombre de pages envoyées à l'extraction, et valeurs utilisées lorsqu'une information est absente du document. Une valeur par défaut n'écrase jamais ce qui a été lu dans le document : elle préremplit le formulaire de validation, où vous pouvez la corriger.",
          )
        }}
      </p>
    </div>

    <div class="px-5 py-4 sm:py-6">
      <div v-for="flow in flows" :key="flow.key" class="mb-6 last:mb-0">
        <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {{ flow.label }}
        </h4>

        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200">
          <Disclosure
            v-for="(documentType, index) in flow.documentTypes"
            :key="documentType"
            v-slot="{ open }"
          >
            <DisclosureButton
              class="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
            >
              <ChevronRightIcon
                class="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200"
                :class="open ? 'rotate-90' : ''"
              />
              <span class="text-sm font-semibold text-gray-900">
                {{ documentTypeLabel(documentType) }}
              </span>
              <span
                class="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10"
              >
                {{ $gettext("Étape %{n}", { n: String(index + 1) }) }}
              </span>
              <span class="ml-auto text-xs text-gray-500">{{ summaryFor(documentType) }}</span>
            </DisclosureButton>

            <DisclosurePanel class="border-t border-gray-200 bg-gray-50/50 px-4 py-4">
              <div class="grid grid-cols-2 gap-3 sm:max-w-md">
                <ExtractionInput
                  v-model="pagesFor(documentType).firstPages"
                  type="number"
                  :min="0"
                  :max="settings.maxPages.perField"
                  :label="$gettext('Premières pages')"
                />
                <ExtractionInput
                  v-model="pagesFor(documentType).lastPages"
                  type="number"
                  :min="0"
                  :max="settings.maxPages.perField"
                  :label="$gettext('Dernières pages')"
                />
              </div>
              <p v-if="pageError(documentType)" class="mt-1 text-xs text-red-600">
                {{ pageError(documentType) }}
              </p>

              <div v-if="catalogFor(documentType).length" class="mt-4 space-y-3">
                <div
                  v-for="field in catalogFor(documentType)"
                  :key="field.fieldKey"
                  class="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-white p-3 sm:grid-cols-3"
                >
                  <ExtractionInput
                    v-model="draftFor(field).strategy"
                    type="select"
                    :label="field.label"
                    :placeholder="$gettext('Aucune valeur par défaut')"
                    :options="strategyOptions(field)"
                  />

                  <!-- static, numeric -->
                  <ExtractionInput
                    v-if="draftFor(field).strategy === 'static' && field.valueType === 'decimal'"
                    v-model="draftFor(field).config.value"
                    type="number"
                    :label="$gettext('Valeur')"
                  />

                  <!-- static, commission type -->
                  <ExtractionInput
                    v-else-if="
                      draftFor(field).strategy === 'static' && field.valueType === 'commission_type'
                    "
                    v-model="draftFor(field).config.value"
                    type="select"
                    :label="$gettext('Type')"
                    :options="commissionTypeOptions"
                  />

                  <!-- percentage_of -->
                  <ExtractionInput
                    v-else-if="draftFor(field).strategy === 'percentage_of'"
                    v-model="draftFor(field).config.percentage"
                    type="number"
                    :label="$gettext('Pourcentage (%)')"
                  />

                  <!-- date_offset -->
                  <template v-else-if="draftFor(field).strategy === 'date_offset'">
                    <ExtractionInput
                      v-model="draftFor(field).config.months"
                      type="number"
                      :label="$gettext('Mois à ajouter')"
                    />
                    <ExtractionInput
                      v-model="draftFor(field).config.source"
                      type="select"
                      :label="$gettext('À partir de')"
                      :options="dateSourceOptions(field)"
                    />
                  </template>
                </div>
              </div>
              <p v-else class="mt-4 text-xs text-gray-500">
                {{ $gettext("Aucun champ configurable pour ce document.") }}
              </p>
            </DisclosurePanel>
          </Disclosure>
        </div>
      </div>
    </div>

    <div class="flex items-center gap-3 px-5 py-4">
      <button
        type="button"
        :disabled="saving || hasPageError"
        class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-300"
        @click="save"
      >
        {{ $gettext("Enregistrer") }}
      </button>
      <p v-if="errorMessage" class="text-xs text-red-600">{{ errorMessage }}</p>
      <p v-else-if="savedAt" class="text-xs text-green-600">{{ $gettext("Enregistré.") }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useGettext } from "vue3-gettext";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { ChevronRightIcon } from "@heroicons/vue/24/outline";
import { useExtractionSettingsStore } from "@/stores/extractionSettings";
import ExtractionInput from "@/components/ui/ExtractionInput.vue";

const { $gettext } = useGettext();
const store = useExtractionSettingsStore();

const settings = ref(null);
const pages = reactive({});
const drafts = reactive({});
const saving = ref(false);
const savedAt = ref(null);
const errorMessage = ref(null);

const DOCUMENT_TYPE_LABELS = {
  sale_listing: "Mandat de vente",
  purchase_offer: "Offre d'achat",
  purchase_agreement: "Compromis de vente",
  rental_listing: "Mandat de location",
  lease_agreement: "Contrat de bail",
};

// Ordered by transaction phase, not by the API's alphabetical order, so each document
// says where it sits in the flow.
//
// The notarial deed is deliberately absent: its extraction has been unreachable since
// its entry point was removed in favour of the purchase agreement one, so offering a
// setting for it would only advertise a feature nobody can use.
const FLOWS = [
  {
    key: "sale",
    label: "Vente",
    documentTypes: ["sale_listing", "purchase_offer", "purchase_agreement"],
  },
  {
    key: "rental",
    label: "Location",
    documentTypes: ["rental_listing", "lease_agreement"],
  },
];

const STRATEGY_LABELS = {
  static: "Valeur fixe",
  percentage_of: "Pourcentage d'un montant du document",
  date_offset: "Une date du document + N mois",
};

const DATE_SOURCE_LABELS = {
  signature_date: "la date de signature",
  today: "la date d'extraction",
};

const documentTypeLabel = (type) => $gettext(DOCUMENT_TYPE_LABELS[type] ?? type);

const knownDocumentTypes = computed(
  () => new Set((settings.value?.documentSettings ?? []).map((s) => s.documentType)),
);

// Only render flows/documents the API actually returned, so a document type removed
// from the catalog can't leave a broken section behind.
const flows = computed(() =>
  FLOWS.map((flow) => ({
    ...flow,
    label: $gettext(flow.label),
    documentTypes: flow.documentTypes.filter((type) => knownDocumentTypes.value.has(type)),
  })).filter((flow) => flow.documentTypes.length > 0),
);

const catalogFor = (documentType) =>
  (settings.value?.catalog ?? []).filter((f) => f.documentType === documentType);

const commissionTypeOptions = computed(() =>
  (settings.value?.commissionTypes ?? []).map((t) => ({ value: t.id, label: t.name })),
);

// An empty strategy means "no default": the field simply stays empty, as today.
const strategyOptions = (field) => [
  { value: "", label: $gettext("Aucune valeur par défaut") },
  ...field.strategies.map((s) => ({ value: s, label: $gettext(STRATEGY_LABELS[s] ?? s) })),
];

const dateSourceOptions = (field) =>
  (field.dateSources ?? []).map((s) => ({ value: s, label: $gettext(DATE_SOURCE_LABELS[s] ?? s) }));

const pagesFor = (documentType) => pages[documentType];

const draftKey = (field) => `${field.documentType}.${field.fieldKey}`;
const draftFor = (field) => drafts[draftKey(field)];

// Shown on the collapsed row, so the section says what it holds without being opened.
const summaryFor = (documentType) => {
  const page = pages[documentType];
  if (!page) return "";

  const configured = catalogFor(documentType).filter(
    (field) => draftFor(field)?.strategy !== "",
  ).length;

  const pagesLabel = $gettext("%{first} + %{last} pages", {
    first: String(page.firstPages),
    last: String(page.lastPages),
  });
  if (configured === 0) return pagesLabel;

  return `${pagesLabel} · ${$gettext("%{n} valeur(s) par défaut", { n: String(configured) })}`;
};

const pageError = (documentType) => {
  const page = pages[documentType];
  if (!page) return null;

  const total = Number(page.firstPages || 0) + Number(page.lastPages || 0);
  if (total === 0) return $gettext("Au moins une page doit être sélectionnée.");
  if (total > settings.value.maxPages.total)
    return $gettext("Le total ne peut pas dépasser %{max} pages.", {
      max: String(settings.value.maxPages.total),
    });

  return null;
};

const hasPageError = computed(() =>
  Object.keys(pages).some((documentType) => pageError(documentType) !== null),
);

const load = (data) => {
  settings.value = data;

  data.documentSettings.forEach((setting) => {
    pages[setting.documentType] = {
      firstPages: setting.firstPages,
      lastPages: setting.lastPages,
    };
  });

  data.catalog.forEach((field) => {
    const existing = data.fieldDefaults.find(
      (d) => d.documentType === field.documentType && d.fieldKey === field.fieldKey,
    );
    drafts[draftKey(field)] = {
      strategy: existing?.strategy ?? "",
      config: { ...(existing?.config ?? {}) },
    };
  });
};

const buildPayload = () => ({
  documentSettings: Object.entries(pages).map(([documentType, page]) => ({
    documentType,
    firstPages: page.firstPages,
    lastPages: page.lastPages,
  })),
  fieldDefaults: (settings.value.catalog ?? [])
    .filter((field) => draftFor(field).strategy !== "")
    .map((field) => ({
      documentType: field.documentType,
      fieldKey: field.fieldKey,
      strategy: draftFor(field).strategy,
      config: draftFor(field).config,
      enabled: true,
    })),
});

const save = async () => {
  saving.value = true;
  errorMessage.value = null;
  savedAt.value = null;

  await store.update(
    buildPayload(),
    (data) => {
      load(data);
      saving.value = false;
      savedAt.value = new Date();
    },
    (errors) => {
      saving.value = false;
      errorMessage.value =
        Object.values(errors?.value ?? {})[0] ??
        $gettext("Les paramètres n'ont pas pu être enregistrés.");
    },
  );
};

onMounted(async () => {
  load(await store.fetch());
});
</script>
